import useStructureParams from '../lib/useStructureParams'
import { structChildren, StructMetric, StructPeriod, StructType, StructureTypeTextFormat } from '../lib/structures'
import { Container, Row, Col, Form } from 'react-bootstrap'

import PageTitle from './PageTitle'
import Breadcrumbs from './Breadcrumbs'
import PageHeader from './PageHeader'
import ViewButtons from './ViewButtons'
import Table from 'react-bootstrap/Table'

import Map from './ActiontMap'

import { LoadingSpinner } from './spinners'
import { NoAssignedWardsMessage } from './alerts'
import { formatInt, getMetricNum, mapStyle, scales } from '../lib/utils'

import {
  Chart as ChartJS,
  registerables
} from 'chart.js'
import 'chartjs-adapter-date-fns'
import annotationPlugin from 'chartjs-plugin-annotation'
import { useState } from 'react'
import { useAction } from '../data/useAction'
import HeaderLayout from "./HeaderLayout"

ChartJS.register(annotationPlugin, ...registerables)

export default function ActiontMapView (): JSX.Element {
  const [selectedChildType, setSelectedChildType] = useState<StructType | undefined>()
  const [period, setPeriod] = useState<StructPeriod>('last30days')
  const [metric, setMetric] = useState<StructMetric>('both')
  const [showBoundaries, setShowBoundaries] = useState<boolean>(false)
  // get parameters from the URL
  const { code, type } = useStructureParams()

  // get stats from the GraphQL API
  const stats = useAction({ type, code, childType: selectedChildType, uniquePeriod: 'daily' })

  // this happens while the query is still fetching
  if (stats == null) {
    return <LoadingSpinner />
  }

  const mapColor = (code: string): string => {
    const c = stats.children.find(c => c.code === code)
    if (c == null) return '#ffffff'
    let scale: keyof typeof scales = 'blue'
    if (c.type === 'votingdistrict' && c.labels.includes('Microtargeting with Registration')) {
      scale = 'orange'
    } else if (c.type === 'votingdistrict' && c.labels.includes('Microtargeting Only')) {
      scale = 'green'
    }
    const color = mapStyle(getMetricNum(c, metric, period), period, (c.registration?.population ?? 0), scale).backgroundColor
    return color
  }

  if (!stats.structureHasAssignedWards) {
    return (
      <>
        <PageTitle title={stats.name} />
        <Breadcrumbs structure={stats} suffix='/action' />
        {/* <PageHeader structure={stats} />
        <ViewButtons structure={stats} section='action' /> */}

        <NoAssignedWardsMessage type={stats.type} />

        <HeaderLayout
           element1={<PageHeader structure={stats} />}
           element2={<ViewButtons structure={stats} section="action" />}
        />
      </>
    )
  } else {
    return (
      <Container fluid className='pl-0 pr-0'>
        <PageTitle title={stats.name} />
        <Breadcrumbs structure={stats} suffix='/action' />
        {/* <PageHeader structure={stats} />
        <ViewButtons structure={stats} section='action' hideDownload /> */}
        <HeaderLayout
           element1={<PageHeader structure={stats} />}
           element2={<ViewButtons structure={stats} section="action" />}
        />
        <Row>
          <Col xs={2}>
            <Form.Group controlId='exampleForm.ControlSelect1'>
              <Form.Label>Period:</Form.Label>
              <Form.Control as='select' value={period} onChange={(e) => setPeriod(e.target.value as StructPeriod)}>
                <option value='last30days'>Last 30 days</option>
                <option value='last7days'>Last 7 days</option>
                <option value='current'>Campaign To Date</option>
              </Form.Control>
            </Form.Group>

            <Form.Group controlId='exampleForm.ControlSelect1'>
              <Form.Label>Metric:</Form.Label>
              <Form.Control as='select' value={metric} onChange={(e) => setMetric(e.target.value as StructMetric)}>
                <option value='activity'>Activity</option>
                <option value='canvassing'>Canvassing</option>
                <option value='both'>Activity & Canvassing</option>
              </Form.Control>
            </Form.Group>
            <Form.Group controlId='exampleForm.ControlSelect1'>
              <Form.Label>Level of detail:</Form.Label>
              <Form.Control as='select' value={selectedChildType} onChange={(e) => setSelectedChildType(e.target.value as StructType)}>
                <option disabled value=''>Select a level...</option>
                {structChildren[type].children.map((level, i) => (level !== 'country') && (
                  <option key={i} value={level}>{StructureTypeTextFormat[level]}</option>
                ))}
              </Form.Control>
            </Form.Group>
            <Form>
              <Form.Label>Layers:</Form.Label>
              <Form.Check
                type='switch'
                className='map-boundaries-switch'
                id='map-boundaries-switch'
                label='Show Boundaries'
                checked={showBoundaries}
                onChange={(e) => setShowBoundaries(e.target.checked)}
              />
            </Form>
            <Table size='sm' responsive striped className='break-before stats'>
              <thead><th className='border-0'>{stats.name}</th></thead>
              <tbody>
                <tr>
                  <td className='text-center'><small><b>Daily Unique Canvassing</b></small></td>
                  <td className='text-center'><small>{formatInt(stats.canvassing[period] ?? '-')}</small></td>
                </tr>
                <tr>
                  <td className='text-center'><small><b>Activity Attendance</b></small></td>
                  <td className='text-center'><small>{formatInt(stats.activity.attendance[period] ?? '-')}</small></td>
                </tr>
              </tbody>
            </Table>
            {selectedChildType === 'votingdistrict'
              ? (
                <Table size='sm' responsive className='stats'>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'center' }}>VD type</th>
                      <th colSpan={3}>less</th>
                      <th colSpan={3} style={{ textAlign: 'right' }}>more</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><small>Crucial</small></td>
                      {scales.blue.map((color, i) => (
                        <td key={`blue-${i}`} style={{ backgroundColor: color, width: '10%' }} />
                      ))}
                    </tr>
                    <tr>
                      <td><small>Focus</small></td>
                      {scales.orange.map((color, i) => (
                        <td key={`orange-${i}`} style={{ backgroundColor: color, width: '10%' }} />
                      ))}
                    </tr>
                    <tr>
                      <td><small>Growth</small></td>
                      {scales.green.map((color, i) => (
                        <td key={`green-${i}`} style={{ backgroundColor: color, width: '10%' }} />
                      ))}
                    </tr>
                  </tbody>
                </Table>
                )
              : (
                <Table size='sm' responsive className='stats'>
                  <thead>
                    <tr>
                      <th colSpan={3}>less</th>
                      <th colSpan={3} style={{ textAlign: 'right' }}>more</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      {scales.blue.map((color, i) => (
                        <td key={`blue-${i}`} style={{ backgroundColor: color, width: '16.666667%', height: '50px' }} />
                      ))}
                    </tr>
                  </tbody>
                </Table>
                )}
          </Col>
          <Col className='p-0'>
            <Map metric={metric} period={period} type={type} code={code} childStructures={stats.children} childColourFn={mapColor} childTypeOverride={selectedChildType} showBoundaries={showBoundaries} />
          </Col>
        </Row>
      </Container>
    )
  }
}
